import type { SimView } from "../../core/simView";
import type { RenderParams } from "../../sim/params";
import type { DrawContext, LensPlugin } from "../types";

/**
 * Short segments along velocity (state v when present & non-zero scale,
 * else finite-diff from writeSpeeds direction via prev is not on SimView —
 * use velocities buffer).
 */
export const velocityVectorsLens: LensPlugin = {
  id: "velocity_vectors",
  label: "Velocity vectors",
  tier: "lab",
  enabled: (r: RenderParams) => r.showVelocityVectors === true,
  draw(view: SimView, g: DrawContext) {
    const { ctx, screenX, screenY, render } = g;
    const { n, dim, velocities } = view;
    if (n < 1) return;

    // Estimate scale: map world velocity to pixels using plot range
    const plot = Math.max(0.5, render.plotRange);
    const span = Math.min(g.width, g.height) * 0.5;
    const worldToPx = span / plot;
    const scale = worldToPx * 8; // exaggerate for readability
    const maxPix = 28;
    const stride = Math.max(1, Math.ceil(n / 400));

    // Simulation writes projective v every step (state or finite-diff).
    let any = false;
    for (let i = 0; i < Math.min(n, 32); i++) {
      const b = i * dim;
      if (
        Math.abs(velocities[b] ?? 0) + Math.abs(velocities[b + 1] ?? 0) >
        1e-12
      ) {
        any = true;
        break;
      }
    }
    if (!any) return;

    ctx.save();
    ctx.strokeStyle = "rgba(120, 220, 255, 0.55)";
    ctx.lineWidth = 1;
    ctx.lineCap = "round";
    const path = new Path2D();

    for (let i = 0; i < n; i += stride) {
      const b = i * dim;
      const vx = velocities[b] ?? 0;
      const vy = velocities[b + 1] ?? 0;
      const sp = Math.hypot(vx, vy);
      if (sp < 1e-10) continue;
      let px = vx * scale;
      let py = -vy * scale; // screen y flips
      const plen = Math.hypot(px, py);
      if (plen > maxPix) {
        const s = maxPix / plen;
        px *= s;
        py *= s;
      }
      const x0 = screenX[i]!;
      const y0 = screenY[i]!;
      path.moveTo(x0, y0);
      path.lineTo(x0 + px, y0 + py);
    }
    ctx.stroke(path);
    ctx.restore();
  },
};
